import { useContext, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AuthContext } from '../../../Context'
import { AuthContextApi } from '../../../Context/api'
import {
    BuildingOfficeIcon,
    WrenchScrewdriverIcon,
    UsersIcon,
    PlusIcon,
    ArrowLeftIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline'

import ObrasTable from '../../../Components/Table/Obras'
import InvestidoresInvestimentosTable from '../../../Components/Table/InvestidoresInvestimentos'
import FormBusca from '../../../Components/Forms/Busca'
import ModalObra from '../../../Components/Modal/Obras'
import VisualizacaoObra from '../../../Components/Modal/Obras/Visualizacao'
import ModalVincularInvestidor from '../../../Components/Modal/InvestidoresInvestimentos'
import VisualizacaoInvestidorVinculado from '../../../Components/Modal/InvestidoresInvestimentos/Visualizacao'
import ModalVincularObrasInvestidor from '../../../Components/Modal/ObraInvestidores/VincularObrasInvestidor'

const TAB_OBRAS = 'obras'
const TAB_INVESTIDORES = 'investidores'

const OBRAS_FILTER_OPTIONS = [
    { value: 'nome_obra', label: 'Nome' },
    { value: 'codigo_obra', label: 'Código' },
    { value: 'descricao', label: 'Descrição' },
]

const OBRAS_SORT_OPTIONS = [
    { value: 'data_criacao', label: 'Data de criação' },
    { value: 'data_atualizacao', label: 'Data de atualização' },
    { value: 'nome_obra', label: 'Nome' },
    { value: 'codigo_obra', label: 'Código' },
    { value: 'data_inicio', label: 'Data de início' },
    { value: 'data_fim_previsto', label: 'Fim previsto' },
]

const Investimento = () => {
    const { id: empreendimentoId } = useParams()
    const navigate = useNavigate()
    const { states, setters } = useContext(AuthContext)
    const {
        getObras, postObras, patchObras,
        getInvestidoresInvestimentos,
        postInvestimentosInvestidores,
        patchInvestimentoStatusBloquearDesbloquear,
        fetchObras,
        fetchInvestidores,
        postObraInvestidor,
    } = useContext(AuthContextApi)

    const [tab, setTab] = useState(TAB_OBRAS)

    // ── Obras ──
    const [obraSearchTerm, setObraSearchTerm] = useState('')
    const [obraFilterField, setObraFilterField] = useState(OBRAS_FILTER_OPTIONS[0].value)
    const [obraSortField, setObraSortField] = useState(OBRAS_SORT_OPTIONS[0].value)
    const [obraSortDirection, setObraSortDirection] = useState('desc')
    const [obraPage, setObraPage] = useState(0)
    const [obraPerPage, setObraPerPage] = useState(10)

    // ── Investidores vinculados ──
    const [invPage, setInvPage] = useState(0)
    const [invPerPage, setInvPerPage] = useState(10)

    // ── Busca para vincular investidor ──
    const [investidoresDisponiveis, setInvestidoresDisponiveis] = useState([])
    const [loadingSearch, setLoadingSearch] = useState(false)

    const [modalObrasInvestidor, setModalObrasInvestidor] = useState(false)
    const [loadingObrasModal, setLoadingObrasModal] = useState(false)
    const [obrasParaVincular, setObrasParaVincular] = useState([])
    const [vinculoObrasCtx, setVinculoObrasCtx] = useState(null)

    // ─────────────────────────────── Obras ───────────────────────────────
    const buildObrasParams = (overrides = {}) => {
        const params = {
            empreendimento_id: empreendimentoId,
            page: overrides.page ?? obraPage,
            per_page: overrides.recordsPerPage ?? overrides.per_page ?? obraPerPage,
            sort: overrides.selectedSortField ?? overrides.sort ?? obraSortField,
            sort_dir: overrides.sortDirection ?? overrides.sort_dir ?? obraSortDirection,
        }
        const term = overrides.searchTerm !== undefined ? overrides.searchTerm : obraSearchTerm
        const filter = overrides.selectedFilterField ?? overrides.filter ?? obraFilterField
        if (term?.trim()) {
            params.filter = filter
            params.search = term.trim()
        }
        return new URLSearchParams(params).toString()
    }

    const loadObras = (overrides = {}) => getObras(buildObrasParams(overrides))

    const searchObras = (formData) => {
        setObraPage(0)
        loadObras({
            page: 0,
            searchTerm: formData.searchTerm,
            selectedFilterField: formData.selectedFilterField,
            selectedSortField: formData.selectedSortField,
            sortDirection: formData.sortDirection,
            recordsPerPage: formData.recordsPerPage,
        })
    }

    const clearObrasSearch = () => {
        setObraSearchTerm('')
        setObraFilterField(OBRAS_FILTER_OPTIONS[0].value)
        setObraSortField(OBRAS_SORT_OPTIONS[0].value)
        setObraSortDirection('desc')
        setObraPerPage(10)
        setObraPage(0)
        loadObras({
            page: 0,
            recordsPerPage: 10,
            selectedSortField: OBRAS_SORT_OPTIONS[0].value,
            sortDirection: 'desc',
            selectedFilterField: OBRAS_FILTER_OPTIONS[0].value,
            searchTerm: '',
        })
    }

    const goToObraPage = (newPage) => {
        setObraPage(newPage)
        loadObras({ page: newPage })
    }

    const addObra = () => {
        setters.setIsEdit(false)
        setters.setObras({ empreendimento_id: empreendimentoId })
        setters.setModalObras(true)
    }

    const editObra = (data) => {
        setters.setIsEdit(true)
        setters.setObras(data)
        setters.setModalObras(true)
    }

    const viewObra = (data) => {
        setters.setObras(data)
        setters.setModalViewObras(true)
    }

    const openObraPainel = (data) => {
        navigate(`/investimentos/investimento/${empreendimentoId}/obra/${data.id}`)
    }

    const createObraHandler = async (data) => {
        const ok = await postObras({ ...data, empreendimento_id: empreendimentoId })
        if (ok) { setters.setModalObras(false); loadObras() }
    }

    const editObraHandler = async (id, data) => {
        const ok = await patchObras(id, data)
        if (ok) { setters.setModalObras(false); setters.setIsEdit(false); loadObras() }
    }

    // ─────────────────────────── Investidores vinculados ───────────────────
    const buildInvParams = (overrides = {}) => new URLSearchParams({
        empreendimento_id: empreendimentoId,
        page: invPage,
        per_page: invPerPage,
        ...overrides,
    }).toString()

    const loadInvestidoresVinculados = (overrides = {}) => getInvestidoresInvestimentos(buildInvParams(overrides))

    const loadInvestidoresRecentes = async () => {
        setLoadingSearch(true)
        const data = await fetchInvestidores(new URLSearchParams({
            page: 0,
            per_page: 5,
            sort: 'data_criacao',
            sort_dir: 'desc',
        }).toString())
        setInvestidoresDisponiveis(data)
        setLoadingSearch(false)
    }

    const addVinculo = () => {
        setters.setInvestidoresInvestimentos({ empreendimento_id: empreendimentoId })
        setters.setModalInvestidoresInvestimentos(true)
        loadInvestidoresRecentes()
    }

    const viewVinculo = (item) => {
        setters.setInvestidoresInvestimentos(item)
        setters.setModalViewInvestidoresInvestimentos(true)
    }

    const searchInvestidorParaVincular = async (term) => {
        setLoadingSearch(true)
        const data = await fetchInvestidores(new URLSearchParams({
            page: 0,
            per_page: 20,
            sort: 'nome',
            sort_dir: 'asc',
            filter: 'nome',
            search: term,
        }).toString())
        setInvestidoresDisponiveis(data)
        setLoadingSearch(false)
    }

    const closeModalObras = () => {
        setModalObrasInvestidor(false)
        setLoadingObrasModal(false)
        setVinculoObrasCtx(null)
        setObrasParaVincular([])
    }

    const createVinculoHandler = async (data) => {
        const investidorNome = data.investidor_nome
            || investidoresDisponiveis.find((i) => i.id === data.investidor_id)?.nome
            || ''
        const { investidor_nome: _nome, ...payload } = data
        const ok = await postInvestimentosInvestidores(payload)
        if (ok) {
            setters.setModalInvestidoresInvestimentos(false)
            setInvestidoresDisponiveis([])
            loadInvestidoresVinculados()

            setVinculoObrasCtx({
                investidor_id: data.investidor_id,
                investidorNome,
            })
            setObrasParaVincular([])
            setLoadingObrasModal(true)
            setModalObrasInvestidor(true)

            const obras = await fetchObras(new URLSearchParams({
                empreendimento_id: empreendimentoId,
                page: 0,
                per_page: 100,
                sort: 'nome_obra',
                sort_dir: 'asc',
            }).toString())

            setObrasParaVincular(obras)
            setLoadingObrasModal(false)
        }
    }

    const processObraQueue = async (fila, { silent = false } = {}) => {
        if (!vinculoObrasCtx?.investidor_id) return { allOk: false }
        let allOk = true
        for (const item of fila) {
            const res = await postObraInvestidor({
                obra_id: item.obra_id,
                investidor_id: vinculoObrasCtx.investidor_id,
                porcentagem: item.porcentagem,
            }, { silent })
            if (!res.ok) allOk = false
        }
        return { allOk }
    }

    const toggleStatusHandler = async (item) => {
        const acao = item.ativo ? 'bloquear' : 'desbloquear'
        setters.setModalMsg(true)
        setters.setMsgModalMsg(`Deseja mesmo ${acao} o acesso de ${item.nome_investidor}?`)
        setters.setDescModalMsg(`Após a confirmação, o investidor será ${item.ativo ? 'bloqueado' : 'desbloqueado'} deste empreendimento.`)
        setters.setTitleButtonModalMsg('Confirmar')
        setters.setFunctionModalMsg(() => async () => {
            const ok = await patchInvestimentoStatusBloquearDesbloquear({
                id: item.id,
                ativo: item.ativo ? 0 : 1,
                empreendimento_id: empreendimentoId,
                investidor_id: item.investidor_id,
            })
            if (ok) {
                setters.setModalMsg(false)
                loadInvestidoresVinculados()
            }
        })
    }

    // ──────────────────────────── Effects ────────────────────────────────
    useEffect(() => {
        if (!empreendimentoId) return
        loadObras({ page: 0 })
        loadInvestidoresVinculados({ page: 0 })
        return () => {
            setters.setDataObras([])
            setters.setDataInvestidoresInvestimentos([])
        }
    }, [empreendimentoId])

    // Sincroniza resultados de busca de investidores para o modal (fallback legado)
    useEffect(() => {
        if (states.modalInvestidoresInvestimentos && states.dataInvestidores?.length && investidoresDisponiveis.length === 0) {
            setInvestidoresDisponiveis(states.dataInvestidores)
        }
    }, [states.dataInvestidores, states.modalInvestidoresInvestimentos])

    // Páginas de obras — paginação manual via goToObraPage

    // Páginas de investidores
    useEffect(() => {
        if (states.dataInvestidoresInvestimentos?.length > 0) loadInvestidoresVinculados()
    }, [invPage, invPerPage])

    // ─────────────────────────────── UI ──────────────────────────────────
    const tabs = [
        { id: TAB_OBRAS, label: 'Obras', icon: WrenchScrewdriverIcon },
        { id: TAB_INVESTIDORES, label: 'Investidores Vinculados', icon: UsersIcon },
    ]

    return (
        <>
            {/* ── Page header ── */}
            <div className="bg-white border-b border-gray-100 px-6 py-5">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/investimentos')}
                        className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5" />
                    </button>
                    <div className="p-2 bg-gray-900 rounded-xl">
                        <BuildingOfficeIcon className="w-5 h-5 text-brand-yellow" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">Painel do Empreendimento</h1>
                        <p className="text-sm text-gray-400">Gerencie obras e investidores deste empreendimento</p>
                    </div>
                </div>
            </div>

            <div className="px-6 pt-5 pb-6 space-y-6">

                {/* ── Tabs ── */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-fit">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${tab === id
                                ? 'bg-gray-900 text-brand-yellow shadow-md'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Icon className="w-4 h-4" />{label}
                        </button>
                    ))}
                </div>

                {/* ── Obras ── */}
                {tab === TAB_OBRAS && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Obras</h2>
                                <p className="text-sm text-gray-400">Gerencie as obras deste empreendimento</p>
                            </div>
                            <button
                                onClick={addObra}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-sm hover:shadow-md transition-all hover:scale-105"
                            >
                                <PlusIcon className="w-4 h-4" /> Nova Obra
                            </button>
                        </div>

                        <FormBusca
                            filterOptions={OBRAS_FILTER_OPTIONS}
                            sortOptions={OBRAS_SORT_OPTIONS}
                            search={searchObras}
                            handleClear={clearObrasSearch}
                            color="yellow"
                            haveData={(states.dataObras ?? []).length > 0}
                            items={{
                                searchTerm: obraSearchTerm,
                                setSearchTerm: setObraSearchTerm,
                                selectedFilterField: obraFilterField,
                                setSelectedFilterField: setObraFilterField,
                                selectedSortField: obraSortField,
                                setSelectedSortField: setObraSortField,
                                sortDirection: obraSortDirection,
                                setSortDirection: setObraSortDirection,
                                recordsPerPage: obraPerPage,
                                setRecordsPerPage: setObraPerPage,
                            }}
                        />

                        <ObrasTable
                            data={states.dataObras ?? []}
                            loading={states.isLoading}
                            page={obraPage}
                            recordsPerPage={obraPerPage}
                            viewObra={viewObra}
                            editObra={editObra}
                            openObraPainel={openObraPainel}
                            next={() => goToObraPage(obraPage + 1)}
                            previous={() => goToObraPage(Math.max(0, obraPage - 1))}
                        />
                    </div>
                )}

                {/* ── Investidores vinculados ── */}
                {tab === TAB_INVESTIDORES && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Investidores Vinculados</h2>
                                <p className="text-sm text-gray-400">Investidores participantes deste empreendimento</p>
                            </div>
                            <button
                                onClick={addVinculo}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-sm hover:shadow-md transition-all hover:scale-105"
                            >
                                <UserPlusIcon className="w-4 h-4" /> Vincular Investidor
                            </button>
                        </div>

                        <InvestidoresInvestimentosTable
                            data={states.dataInvestidoresInvestimentos ?? []}
                            loading={states.isLoading}
                            page={invPage}
                            recordsPerPage={invPerPage}
                            viewVinculo={viewVinculo}
                            toggleStatus={toggleStatusHandler}
                            next={() => setInvPage(p => p + 1)}
                            previous={() => setInvPage(p => Math.max(0, p - 1))}
                        />
                    </div>
                )}
            </div>

            {/* ── Modais ── */}
            <ModalObra
                states={states}
                setters={setters}
                actionCreate={createObraHandler}
                actionEdit={editObraHandler}
                empreendimentoId={empreendimentoId}
            />
            <VisualizacaoObra states={states} setters={setters} />

            <ModalVincularInvestidor
                states={states}
                setters={setters}
                actionCreate={createVinculoHandler}
                onSearchInvestidor={searchInvestidorParaVincular}
                investidoresDisponiveis={investidoresDisponiveis}
                loadingSearch={loadingSearch}
            />
            <VisualizacaoInvestidorVinculado
                states={states}
                setters={setters}
            />

            <ModalVincularObrasInvestidor
                open={modalObrasInvestidor}
                onClose={closeModalObras}
                loading={loadingObrasModal}
                investidorNome={vinculoObrasCtx?.investidorNome}
                obras={obrasParaVincular}
                onProcessQueue={processObraQueue}
            />
        </>
    )
}

export default Investimento
